import { type ReactNode } from 'react';

interface StatCardProps {
  /** Large statistic number — can include prefix/suffix like "75%", "<25%", "$2B" */
  number: string;
  /** Short description of what the stat represents */
  label: string;
  /** Optional source citation (shown smaller and muted) */
  source?: string;
  /** Accent color for the left bar and number. Default: #074EDC (blue) */
  accent?: string;
  /** Optional className for the outer wrapper */
  className?: string;
  /** Optional additional children (e.g. extra context below the label) */
  children?: ReactNode;
}

/**
 * StatCard — large-stat display with left accent bar, used for market-research
 * and key-number callouts. The dark glass card pattern matches all other
 * infographic components.
 *
 * Accepts an optional `children` slot so a card can carry extra rich content
 * beneath the label (e.g. a tiny chart icon, a list, or an inline link).
 */
export function StatCard({
  number,
  label,
  source,
  accent = '#074EDC',
  className = '',
  children,
}: StatCardProps) {
  return (
    <div
      className={`rounded-2xl border border-white/15 bg-[rgba(2,8,28,0.88)] backdrop-blur-2xl p-8 border-l-4 ${className}`}
      style={{ borderLeftColor: accent }}
    >
      <div
        className="font-display font-black text-4xl md:text-5xl leading-none"
        style={{ color: accent }}
      >
        {number}
      </div>
      <p className="mt-3 text-white/75 text-sm leading-snug">
        {label}
      </p>
      {source && (
        <p className="mt-3 text-white/35 text-xs italic leading-relaxed">
          — {source}
        </p>
      )}
      {children && (
        <div className="mt-4 border-t border-white/10 pt-4">
          {children}
        </div>
      )}
    </div>
  );
}
